import "reflect-metadata";
import { Environment } from "$lib/environment/environment";
import { handleErrorWithSentry } from "@sentry/sveltekit";
import * as Sentry from "@sentry/sveltekit";
import mixpanel from "mixpanel-browser";
import posthog from "posthog-js";
import { MIXPANEL_TOKEN } from "shared/src/statics";
import {
  captureAndStripEventGuestInvite,
  getSessionStorageSafely,
  stripSensitiveUrl,
} from "$lib/security/eventGuestInviteHandoff";
import { dropReplayNetworkCapture } from "$lib/security/telemetryPrivacy";

const environment = new Environment();

import LogRocket from "logrocket";

// This must run before every analytics/session-replay init below. Guest invite
// capabilities arrive in a fragment specifically so the server never sees
// them; leaving that fragment in the browser even briefly would expose it to
// client telemetry initialization.
if (typeof window !== "undefined") {
  captureAndStripEventGuestInvite({
    location: window.location,
    history: window.history,
    storage: getSessionStorageSafely(window),
  });
}

posthog.init("phc_VknwfNxufZYAOkfogHH8aa2NL9Ll0PkUVZjMPXARjuM", {
  api_host: "https://app.posthog.com",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  mask_all_text: true,
  mask_all_element_attributes: true,
});

mixpanel.init(MIXPANEL_TOKEN, {
  debug: false,
  track_pageview: false,
  persistence: "localStorage",
  // Guest names, answers, invite QR codes, and capability-bearing links must
  // never enter third-party replay recordings.
  record_sessions_percent: 0,
  record_mask_text_selector: "*",
  record_block_selector: ".moodstir-private",
});

if (environment.getEnvironment() === "production") {
  LogRocket.init("gzkjdy/moodstir", {
    shouldParseXHRBlob: false,
    browser: {
      urlSanitizer: stripSensitiveUrl,
    },
    dom: {
      textSanitizer: true,
      inputSanitizer: true,
      privateClassNameBlocklist: ["moodstir-private"],
      privateAttributeBlocklist: ["data-moodstir-private"],
      hiddenAttributes: ["href", "src"],
    },
    network: {
      // Network bodies can contain capabilities, Firebase credentials, guest
      // answers, or photos. Disable replay capture for every request/response;
      // endpoint allowlists are too easy to make incomplete over time.
      requestSanitizer: dropReplayNetworkCapture,
      responseSanitizer: dropReplayNetworkCapture,
    },
  });

  Sentry.init({
    environment: environment.getEnvironment(),
    dsn: "https://224b69a4f2a27b55d94c768fcc6887de@o4505926245679104.ingest.sentry.io/4505926248955904",
    tracesSampleRate: 1.0,
    sendDefaultPii: false,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0,

    // If you don't want to use Session Replay, just remove the line below:
    integrations: [],
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = stripSensitiveUrl(String(breadcrumb.data.url));
      }
      if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
        breadcrumb.data = breadcrumb.data?.url
          ? { url: breadcrumb.data.url, status_code: breadcrumb.data.status_code }
          : undefined;
      }
      return breadcrumb;
    },
    beforeSend: (event) => {
      if (event.request) {
        event.request.url = event.request.url
          ? stripSensitiveUrl(event.request.url)
          : event.request.url;
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.query_string;
      }
      return event;
    },
  });
}

// If you have a custom error handler, pass it to `handleErrorWithSentry`
export const handleError = handleErrorWithSentry();
